/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useState } from 'react'
import { CheckIcon, CrownIcon, LogOutIcon, RefreshCwIcon, ShieldIcon, UserPlusIcon, UsersIcon, XIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, MOTION, RADII, SHADOWS, TYPE, space } from '../theme'
import { PARTY, partyLobby, refreshScreens, screenData, subscribeScreenData, worldStatus } from '../screen-data'
import { Btn, MetaStat, Panel, ScreenShell } from './VoiceKit'

export function PartyLobbyScreen({ world, onClose }) {
  const [state, setState] = useState(() => partyLobby(world))
  const [loaded, setLoaded] = useState(() => screenData(world, 'party') !== null)
  const [name, setName] = useState('')
  const [pending, setPending] = useState(null)
  const status = worldStatus(world)

  useEffect(() => {
    const off = subscribeScreenData(world, 'party', () => {
      setState(partyLobby(world))
      setLoaded(true)
      setPending(null)
    })
    refreshScreens(world)
    return off
  }, [world])

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const run = (id, action) => {
    if (!action()) {
      world.ui.notify({ kind: 'error', title: 'ДЕЙСТВИЕ НЕ ОТПРАВЛЕНО', message: 'Соединение с миром недоступно.', dedupeKey: 'party:offline' })
      return
    }
    setPending(id)
    setTimeout(() => refreshScreens(world), 700)
  }
  const confirmRun = async (id, title, message, action) => {
    const confirmed = await world.ui.confirm({ kind: 'confirm', tone: 'warning', title, message, safeEnter: false, dedupeKey: `party:${id}:confirm` })
    if (confirmed) run(id, action)
  }

  const party = state.party
  const members = party?.members || []
  const slots = Array.from({ length: party?.maxMembers || 4 }, (_, index) => members[index] || null)

  return (
    <ScreenShell
      label='WORLD'
      title='КОМПАНИЯ И ГРУППА'
      subtitle='Играй вместе. Приглашения проверяет сервер мира.'
      ariaLabel='Компания и группа'
      onClose={onClose}
      meta={
        <>
          <MetaStat icon={<UsersIcon size={ICON_SIZES.sm} color={COLORS.cyan} />} label='ГРУППА' value={party ? `${members.length}/${party.maxMembers}` : 'НЕТ'} />
          <MetaStat icon={<ShieldIcon size={ICON_SIZES.sm} color={COLORS.success} />} label='МИР' value={status.place} />
        </>
      }
    >
      <div
        css={css`
          flex: 1 1 auto;
          min-height: 0;
          padding: ${space(2)};
          display: grid;
          grid-template-columns: minmax(0, 1fr) 320px;
          gap: ${space(2)};
          overflow: hidden;
          @media (max-width: 900px) { grid-template-columns: 1fr; overflow-y: auto; }
          @media (max-width: 600px) { padding: ${space(1)}; }
        `}
      >
        <div css={css`min-height:0;display:flex;flex-direction:column;gap:${space(2)};`}>
          {!loaded ? (
            <Panel title='СИНХРОНИЗАЦИЯ'><Empty icon={<RefreshCwIcon size={ICON_SIZES.lg} />} title='ЗАГРУЖАЕМ ГРУППУ' text='Запрашиваем актуальное состояние у сервера мира.' /></Panel>
          ) : !state.available ? (
            <Panel title='ГРУППЫ НЕДОСТУПНЫ'><Empty icon={<XIcon size={ICON_SIZES.lg} />} title='НЕТ СЕРВЕРНОГО КАНАЛА' text='Экран не создаёт демонстрационную группу. Повтори запрос после подключения.' /><Btn full onClick={() => refreshScreens(world)}>ПОВТОРИТЬ</Btn></Panel>
          ) : !party ? (
            <Panel title='СОЗДАТЬ КОМПАНИЮ' info='Группа сохраняется в состоянии мира и восстановится после reconnect.'>
              <div css={css`display:flex;flex-direction:column;gap:${space(2)};max-width:520px;margin:auto;padding:${space(3)} 0;`}>
                <Empty icon={<UsersIcon size={ICON_SIZES.lg} />} title='ТЫ ПОКА БЕЗ ГРУППЫ' text='Создай компанию или прими одно из адресованных тебе приглашений.' />
                <label css={css`display:flex;flex-direction:column;gap:${space(1)};font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`}>
                  НАЗВАНИЕ ГРУППЫ
                  <input
                    value={name}
                    maxLength={32}
                    onChange={event => setName(event.target.value)}
                    placeholder='Например: Ночная прогулка'
                    css={inputCss}
                  />
                </label>
                <Btn full disabled={!name.trim() || pending === 'create'} onClick={() => run('create', () => PARTY.create(world, name.trim()))}>
                  <UsersIcon size={ICON_SIZES.xs} /> {pending === 'create' ? 'СОЗДАЁМ…' : 'СОЗДАТЬ ГРУППУ'}
                </Btn>
              </div>
            </Panel>
          ) : (
            <>
              <Panel title={`${party.name} · ${members.length}/${party.maxMembers}`} info='Лидер отмечен короной. Офлайн-участники остаются в группе и вернутся после reconnect.'>
                <div css={css`display:grid;grid-template-columns:repeat(4,minmax(120px,1fr));gap:${space(2)};@media(max-width:700px){grid-template-columns:repeat(2,1fr);}`}>
                  {slots.map((member, index) => member ? <MemberCard key={member.userId} member={member} /> : <FreeSlot key={`slot-${index}`} />)}
                </div>
              </Panel>
              <div css={css`display:flex;gap:${space(1)};justify-content:flex-end;flex-wrap:wrap;`}>
                {party.isLeader ? (
                  <Btn variant='destructive' disabled={pending === 'disband'} onClick={() => confirmRun('disband', 'РАСПУСТИТЬ ГРУППУ?', 'Все участники покинут текущую группу. Сервер повторно проверит лидерство.', () => PARTY.disband(world))}>
                    <XIcon size={ICON_SIZES.xs} /> {pending === 'disband' ? 'РАСПУСКАЕМ…' : 'РАСПУСТИТЬ ГРУППУ'}
                  </Btn>
                ) : (
                  <Btn variant='destructive' disabled={pending === 'leave'} onClick={() => confirmRun('leave', 'ПОКИНУТЬ ГРУППУ?', 'Лидерство будет передано по правилам сервера мира, если это необходимо.', () => PARTY.leave(world))}>
                    <LogOutIcon size={ICON_SIZES.xs} /> {pending === 'leave' ? 'ВЫХОДИМ…' : 'ПОКИНУТЬ ГРУППУ'}
                  </Btn>
                )}
              </div>
            </>
          )}
        </div>

        <aside css={css`min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:${space(2)};`}>
          <Panel title={`ПРИГЛАШЕНИЯ · ${state.invitations.length}`}>
            {state.invitations.length ? state.invitations.map(invite => (
              <div key={invite.id} css={cardCss}>
                <strong>{invite.fromName}</strong>
                <span css={mutedCss}>Приглашает в «{invite.groupName}»</span>
                <div css={css`display:grid;grid-template-columns:1fr 1fr;gap:${space(1)};`}>
                  <Btn size='small' disabled={pending === invite.id} onClick={() => run(invite.id, () => PARTY.accept(world, invite.id))}><CheckIcon size={ICON_SIZES.xs} /> ПРИНЯТЬ</Btn>
                  <Btn size='small' variant='secondary' disabled={pending === invite.id} onClick={() => run(invite.id, () => PARTY.decline(world, invite.id))}>ОТКЛОНИТЬ</Btn>
                </div>
              </div>
            )) : <Empty title='НЕТ ПРИГЛАШЕНИЙ' text='Здесь появляются только приглашения, адресованные твоему аккаунту.' />}
          </Panel>

          {party?.isLeader && members.length < party.maxMembers && (
            <Panel title={`ИГРОКИ В МИРЕ · ${state.availablePlayers.length}`} info='Нельзя пригласить себя, участника другой группы или отправить дубликат.'>
              {state.availablePlayers.length ? state.availablePlayers.map(player => (
                <div key={player.userId} css={css`${cardCss};display:flex;align-items:center;gap:${space(1)};`}>
                  <OnlineDot />
                  <strong css={css`flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;`}>{player.name}</strong>
                  <Btn size='small' disabled={player.invited || pending === player.userId} onClick={() => run(player.userId, () => PARTY.invite(world, player.userId))}><UserPlusIcon size={ICON_SIZES.xs} /> {player.invited ? 'ПРИГЛАШЁН' : 'ПРИГЛАСИТЬ'}</Btn>
                </div>
              )) : <Empty title='НЕКОГО ПРИГЛАСИТЬ' text='Свободных игроков онлайн сейчас нет.' />}
            </Panel>
          )}
        </aside>
      </div>
    </ScreenShell>
  )
}

function MemberCard({ member }) {
  return <div css={css`${cardCss};min-height:180px;display:grid;place-items:center;align-content:center;text-align:center;gap:${space(1)};border-color:${member.leader ? COLORS.warning : COLORS.border};`}>
    <div css={css`width:64px;height:64px;border-radius:${RADII.round};display:grid;place-items:center;background:${COLORS.bgElevated};border:2px solid ${member.online ? COLORS.success : COLORS.border};font-size:${TYPE.title.size};position:relative;`}>
      {member.name.slice(0, 1).toUpperCase()}
      {member.leader && <CrownIcon size={ICON_SIZES.xs} color={COLORS.warning} css={css`position:absolute;right:-4px;top:-4px;`} />}
    </div>
    <strong>{member.name}</strong>
    <span css={mutedCss}>{member.leader ? 'ЛИДЕР · ' : ''}{member.online ? 'В МИРЕ' : 'ОФЛАЙН'}</span>
  </div>
}

function FreeSlot() {
  return <div css={css`${cardCss};min-height:180px;display:grid;place-items:center;align-content:center;text-align:center;gap:${space(1)};border-style:dashed;color:${COLORS.textSecondary};`}><UserPlusIcon size={ICON_SIZES.lg} /><span css={mutedCss}>СВОБОДНЫЙ СЛОТ</span></div>
}

function Empty({ icon, title, text }) {
  return <div role='status' css={css`min-height:110px;display:grid;place-items:center;align-content:center;gap:${space(1)};text-align:center;color:${COLORS.textSecondary};`}>
    {icon}<strong css={css`color:${COLORS.text};letter-spacing:.1em;`}>{title}</strong>{text && <span css={css`font-size:${TYPE.caption.size};max-width:300px;`}>{text}</span>}
  </div>
}

function OnlineDot() {
  return <span aria-label='В мире' css={css`width:8px;height:8px;border-radius:${RADII.round};background:${COLORS.success};box-shadow:0 0 8px ${COLORS.success};`} />
}

const inputCss = css`
  min-height: 44px;
  padding: 0 ${space(1)};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADII.lg};
  background: ${COLORS.bgPanelLight};
  color: ${COLORS.text};
  font: inherit;
  &:focus { outline: none; border-color: ${COLORS.borderStrong}; box-shadow: ${SHADOWS.focus}; }
`

const cardCss = css`
  padding: ${space(2)};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADII.xl};
  background: ${COLORS.bgPanelLight};
  transition: border-color ${MOTION.hover};
`

const mutedCss = css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`
